/**
 * Approve, then JobCork sends (E17).
 *
 * A form is filled on the employer's page, so something has to run there:
 * the extension in the person's Chrome, or a browser JobCork runs in the
 * cloud. This is the second, for one posting the person chose: they see
 * what would go -- the answers, the resume, the letter, what was saved for
 * this posting -- and press Send. The cloud runner then fills and presses
 * Submit under the same gate and into the same record as Auto, and stops
 * with "needs you" when the form asks what the record cannot answer; the
 * Applications row takes the answer and offers "Send it again".
 *
 * Works in Safari, on a phone, without the extension. The extension stays
 * for anyone who wants to watch the employer's form themselves.
 */

import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "./auth";
import { atsOf } from "./fillHint";
import { runWords, type RunStatus } from "./AutoRun";

interface PacketView {
  job: { jobId: string; title: string; employer: string; ats: string; closed: boolean };
  application: { tracked: boolean; applyMode: string; submissionState: string };
  gate: { canFill: boolean; canSubmit: boolean; failures: Array<{ code: string; message: string; transient: boolean }> };
  answers: {
    confirmedAt: string | null; legalFirstName: string | null; legalLastName: string | null; email: string | null; phone: string | null;
    homeCity: string | null; homeCountry: string | null; workAuthorizedUs: boolean | null; requiresSponsorship: boolean | null;
    highestEducation: string | null; willingToRelocate: boolean | null; earliestStartDate: string | null; desiredCompCents: number | null;
    currentCompany?: string | null; currentTitle?: string | null; acceptStandardConsents?: boolean; letModelAnswer?: boolean;
  };
  contact: { linkedin: string | null; website: string | null; location: string | null };
  resume: { filename: string | null; kind: "tailored" | "original" | null };
  coverLetter: { text: string | null; filename: string | null };
  savedAnswers?: Record<string, string[]>;
}

function describe(err: unknown): string {
  if (err instanceof ApiError) return err.messages.join(" ");
  return err instanceof Error ? err.message : String(err);
}

const yesNo = (v: boolean | null) => (v === null ? "not answered" : v ? "Yes" : "No");

/** The lines "What JobCork will send" shows, from the packet. Pure, so it can be read in a test. */
export function sendLines(p: PacketView): Array<{ label: string; value: string; missing?: boolean }> {
  const a = p.answers;
  const name = [a.legalFirstName, a.legalLastName].filter(Boolean).join(" ");
  const lines: Array<{ label: string; value: string; missing?: boolean }> = [
    { label: "Name", value: name || "not in your answers", missing: !name },
    { label: "Email", value: a.email ?? "not in your answers", missing: !a.email },
    { label: "Phone", value: a.phone ?? "not in your answers", missing: !a.phone },
    { label: "Location", value: [a.homeCity, a.homeCountry].filter(Boolean).join(", ") || p.contact.location || "not in your answers", missing: !a.homeCity && !p.contact.location },
    { label: "Authorized to work in the US", value: yesNo(a.workAuthorizedUs), missing: a.workAuthorizedUs === null },
    { label: "Needs sponsorship", value: yesNo(a.requiresSponsorship), missing: a.requiresSponsorship === null },
    { label: "Resume", value: p.resume.filename ? `${p.resume.filename}${p.resume.kind === "tailored" ? " (tailored for this posting)" : ""}` : "no resume on record", missing: !p.resume.filename },
    { label: "Cover letter", value: p.coverLetter.filename ? p.coverLetter.filename : "none written for this posting — the form's cover letter box is left empty" },
  ];
  if (p.contact.linkedin) lines.push({ label: "LinkedIn", value: p.contact.linkedin });
  if (p.contact.website) lines.push({ label: "Website", value: p.contact.website });
  lines.push({ label: "Employer's own questions", value: a.letModelAnswer ? "written by the model from your resume, sent only when every claim is backed by it" : "come back to you as “needs you” — or let the model answer them (Answers page)" });
  if (a.currentCompany) lines.push({ label: "Current company", value: a.currentCompany });
  if (a.highestEducation) lines.push({ label: "Highest education", value: a.highestEducation });
  const saved = Object.entries(p.savedAnswers ?? {});
  for (const [q, vs] of saved) lines.push({ label: `Saved answer — ${q.charAt(0).toUpperCase()}${q.slice(1)}`, value: vs[0] ?? "" });
  return lines;
}

export function Send({ jobId, applyUrl, title, employer, onOver }: {
  jobId: string;
  applyUrl: string | null | undefined;
  title: string;
  employer: string;
  autoMode?: "auto" | "review";
  /** The run ended: the caller refreshes what it shows. */
  onOver?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [packet, setPacket] = useState<PacketView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [run, setRun] = useState<RunStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState<"send" | "rehearse" | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const over = useRef(onOver);
  over.current = onOver;
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const ats = atsOf(applyUrl);
  if (!applyUrl || ats === null || ats === "workday") return null;

  const look = async () => {
    setOpen(true);
    if (packet) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api<{ known: boolean; packet?: PacketView; errors?: string[] }>(`/api/apply/packet?url=${encodeURIComponent(applyUrl)}`);
      if (!r.known || !r.packet) throw new Error(r.errors?.[0] ?? "This posting is not among your matches.");
      setPacket(r.packet);
    } catch (e) {
      setError(describe(e));
    } finally {
      setLoading(false);
    }
  };

  const watch = (id: string, startedAt: number) => {
    timer.current = setTimeout(async () => {
      try {
        const r = await api<RunStatus>(`/api/auto/runs/${id}`);
        setRun(r);
        const done = r.status === "complete" || r.status === "errored" || r.status === "terminated";
        if (!done && Date.now() - startedAt < 8 * 60_000) watch(id, startedAt);
        else { setBusy(false); over.current?.(); }
      } catch (e) {
        setError(describe(e));
        setBusy(false);
      }
    }, 5_000);
  };

  const send = async () => {
    setAction("send");
    setBusy(true);
    setError(null);
    setRun(null);
    try {
      const r = await api<{ id: string }>(`/api/applications/${jobId}/send`, { method: "POST", body: "{}" });
      setRun({ id: r.id, status: "queued", output: null, error: null });
      watch(r.id, Date.now());
    } catch (e) {
      setError(describe(e));
      setBusy(false);
    }
  };

  // The same fill through the same cloud browser, but dryRun: it stops before
  // Submit, sends nothing and records nothing -- how you see what would go
  // without applying. A per-posting rehearsal bypasses the queue's gate.
  const rehearse = async () => {
    setAction("rehearse");
    setBusy(true);
    setError(null);
    setRun(null);
    try {
      const r = await api<{ id: string }>("/api/auto/runs", { method: "POST", body: JSON.stringify({ jobId, dryRun: true }) });
      setRun({ id: r.id, status: "queued", output: null, error: null });
      watch(r.id, Date.now());
    } catch (e) {
      setError(describe(e));
      setBusy(false);
    }
  };

  const blocking = packet ? packet.gate.failures.filter((f) => f.code !== "needs_user_approval") : [];
  const line = run
    ? { text: busy && !run.output ? "A browser in the cloud is opening the form and filling it… this takes a minute or two." : runWords(run), ok: run.output?.outcome === "submitted" }
    : null;

  if (!open) {
    return (
      <button className="btn ghost" type="button" onClick={() => void look()}>
        Send with JobCork
      </button>
    );
  }

  return (
    <div className="send">
      <div className="send-head">
        <p className="send-title">Review — {title} at {employer}</p>
        {!busy && !run && <button className="step-link small" type="button" onClick={() => setOpen(false)}>Close</button>}
      </div>
      {loading && <p className="muted small">Reading what would be sent…</p>}
      {packet && !run && (
        <>
          <p className="small"><strong>Send application</strong> fills the employer's form from these, in a browser JobCork runs, and presses Submit — a real application to {employer}. <strong>Rehearse</strong> fills it exactly the same way but stops before Submit, so nothing is sent. Anything the form asks that is not here comes back to you as “needs you”.</p>
          <dl className="send-lines small">
            {sendLines(packet).map((l) => (
              <div key={l.label} className={l.missing ? "missing" : ""}>
                <dt>{l.label}</dt>
                <dd>{l.value}</dd>
              </div>
            ))}
          </dl>
          {blocking.length > 0 && (
            <p className="err small">JobCork will not send this one: {blocking.map((f) => f.message).join(" ")}</p>
          )}
          <div className="row">
            <button className="btn" type="button" disabled={busy || blocking.length > 0} onClick={() => void send()}>
              {busy && action === "send" ? "Sending…" : "Send application"}
            </button>
            <button className="btn ghost" type="button" disabled={busy} onClick={() => void rehearse()}>
              {busy && action === "rehearse" ? "Rehearsing…" : "Rehearse — sends nothing"}
            </button>
          </div>
          <p className="muted small">Send is a real application to {employer}; the record shows every field it filled. Rehearse sends nothing and records nothing.</p>
        </>
      )}
      {line && <p className={`small ${line.ok ? "ok" : "muted"}`}>{line.text}</p>}
      {run && !busy && (
        <p className="small"><button className="step-link small" type="button" onClick={() => { setRun(null); setOpen(false); }}>Close</button></p>
      )}
      {error && <p className="err small">{error}</p>}
    </div>
  );
}
